import { plural } from '../../../../shared/helpers/plural';
import queryClient from '../../../ReactQuery/client';
import useToastedMutation from '../../../ReactQuery/useToastedMutation';
import { put } from '../../../redux/api';
import type {
  RemoveProjectsFromTeamRequest,
  RemoveProjectsFromTeamResponse,
} from '../../../../shared/types/routes/teams';
import type { Team } from '../../../types';

import { getTeamQueryKey } from './useTeam';

type RemoveProjectsFromTeamVariables = {
  teamId: number;
  projectIds: string[];
};

//  Actual function to remove projects from a team
async function removeProjectsFromTeam({ teamId, projectIds }: RemoveProjectsFromTeamVariables) {
  const response = await put<RemoveProjectsFromTeamRequest, RemoveProjectsFromTeamResponse>(`/api/teams/${teamId}/projects`, { projects: projectIds, action: 'remove' });
  return response.data as RemoveProjectsFromTeamResponse;
}

/**
 * Hook for removing projects from a team.
 */
export default function useRemoveProjectsFromTeam() {
  return useToastedMutation({
    mutationFn: removeProjectsFromTeam,
    successToastMessage: (_response, variables: RemoveProjectsFromTeamVariables) =>
      `Removed ${variables.projectIds.length} ${plural(variables.projectIds.length, 'project')} from team.`,
    errorToastMessage: 'Failed to remove project(s) from team.',
    onSuccess: (response, variables: RemoveProjectsFromTeamVariables) => {
      // Update the team's user count in the cache
      queryClient.setQueryData(getTeamQueryKey(variables.teamId), (oldTeam?: Team) => {
        if (!oldTeam) {
          return oldTeam;
        }
        return {
          ...oldTeam,
          //  Map the project IDs to the team project objects, as that's what this endpoint returns
          teamProjects: response.projects.map(projectId => ({ projectId })),
        };
      });

      // Also re-fetch the team to get any other changes
      queryClient.invalidateQueries({ queryKey: getTeamQueryKey(variables.teamId) });

      // TODO: we gotta invalidate the projects query, which is actually complicated because it's some bullshit off the org
      // queryClient.invalidateQueries({ queryKey: getProjectsQueryKey() });
    },
  });
}
